import * as B from '@blueprintjs/core';
import {DatePicker} from '@blueprintjs/datetime';
import * as I from 'immutable';
import moment from 'moment-timezone';
import React from 'react';

import {ApiFeature, ApiFeatureCommonProperties} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';

interface Props {
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  /** The properties the change applies to. */
  features: I.ImmutableOf<ApiFeature[]>;
  batchPatchFeature: FeaturesActions['batchPatchFeature'];
  /**
   * The current due date (`YYYY-MM-DD`, or empty for none) when editing one
   * property. Leave unset when the properties may disagree.
   */
  value?: string;
  /** The popover's trigger. */
  children: React.ReactElement;
}

/** Date picker that sets or clears the report due date on every given property. */
export const DueDateMenu: React.FC<Props> = ({
  featureCollection,
  features,
  batchPatchFeature,
  value,
  children,
}) => {
  // `DatePicker` compares `value` by reference, and resets the month it's
  // showing whenever it changes. A new `Date` each render would snap the user
  // back to the stored month mid-browse, on any unrelated re-render.
  const date = React.useMemo(() => {
    if (value === undefined) {
      return undefined;
    }
    const parsed = value ? moment(value, 'YYYY-MM-DD', true) : null;
    return parsed?.isValid() ? parsed.toDate() : null;
  }, [value]);

  // Blueprint reads a click on the already-selected day as a deselect. Against a
  // picker that opens on the current due date that gesture reads as confirming
  // the date, so it's dropped and only Clear clears.
  const clickedSelectedDay = React.useRef(false);

  return (
    <B.Popover
      position={B.Position.BOTTOM_RIGHT}
      modifiers={{arrow: {enabled: false}}}
      captureDismiss
      content={
        <DatePicker
          showActionsBar
          value={date}
          maxDate={moment().add(5, 'years').toDate()}
          dayPickerProps={{
            onDayClick: (_day, modifiers) => {
              clickedSelectedDay.current = !!modifiers.selected && !modifiers.disabled;
            },
          }}
          onChange={(newDate, isUserChange) => {
            const wasDeselect = clickedSelectedDay.current;
            clickedSelectedDay.current = false;

            // With a value set, the picker also reports the shifted date when the
            // user pages to another month; only a picked day is a change.
            if (!isUserChange || (newDate === null && wasDeselect)) {
              return;
            }
            // `newDate` is in the browser's local timezone, which moment preserves.
            const properties: Partial<ApiFeatureCommonProperties> = {
              [CONSTANTS.APP_PROPERTIES_KEY]: {
                [CONSTANTS.APP_PROPERTY_REPORTING_DUE_DATE]: newDate
                  ? moment(newDate).format('YYYY-MM-DD')
                  : '',
              },
            };

            batchPatchFeature({
              featureCollectionId: featureCollection.get('id'),
              features,
              changes: I.fromJS({properties}),
            });

            recordEvent(newDate ? 'Set report due date' : 'Unset report due date', {
              featureCollectionId: featureCollection.get('id'),
              featureCount: features.size,
            });
          }}
        />
      }
    >
      {children}
    </B.Popover>
  );
};
